import axios from 'axios';

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import useAsset from '../components/hook/useAsset';
import { CACHE_KEYS } from './config';
import { CampaignQualification } from './insightsTypes';
import { api } from './swaggerApi';

export type CampaignQualificationTarget =
  CampaignQualification['attributes']['targeting'][0];

export type CampaignQualificationUpdateData = Partial<
  Omit<CampaignQualification['attributes'], 'id'>
>;

export const update = (
  identifier: string,
  data: CampaignQualificationUpdateData
) =>
  axios.put(`/insights_platform/campaign_qualifications/${identifier}`, data);

export const create = (
  data: CampaignQualificationUpdateData & { qualifiable_identifier: string }
) => axios.post(`/insights_platform/campaign_qualifications/`, data);

export const destroy = (identifier: string) =>
  axios.delete(`/insights_platform/campaign_qualifications/${identifier}`);

type CQData = { data: { data: CampaignQualification[] } };

const campaignQualificationUpdater = async (
  queryClient: QueryClient,
  quotaIdentifier: string,
  updater: (oldData: CQData) => CQData
) => {
  await queryClient.cancelQueries([
    CACHE_KEYS.CAMPAIGN_QUALIFICATIONS,
    quotaIdentifier,
  ]);

  const previous = queryClient.getQueryData([
    CACHE_KEYS.CAMPAIGN_QUALIFICATIONS,
    quotaIdentifier,
  ]);

  queryClient.setQueryData(
    [CACHE_KEYS.CAMPAIGN_QUALIFICATIONS, quotaIdentifier],
    (old: CQData) => updater(old)
  );
  return { previous };
};

export const useUpdateCampaignQualification = (
  quotaIdentifier: string,
  identifier: string
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (data: CampaignQualificationUpdateData) => update(identifier, data),
    {
      // Optimistic update
      onMutate: async (data) => {
        return campaignQualificationUpdater(
          queryClient,
          quotaIdentifier,
          (old) => {
            const newCqs = old.data.data.map((cq) => {
              if (cq.attributes.identifier === identifier) {
                return { ...cq, attributes: { ...cq.attributes, ...data } };
              }
              return cq;
            });
            return { data: { data: newCqs } };
          }
        );
      },

      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, newTodo, context: { previous: unknown }) => {
        queryClient.setQueryData(
          [CACHE_KEYS.CAMPAIGN_QUALIFICATIONS, quotaIdentifier],
          context.previous
        );
      },

      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries([
          CACHE_KEYS.CAMPAIGN_QUALIFICATIONS,
          quotaIdentifier,
        ]);
      },
    }
  );

  return mutation;
};

export const useCreateCampaignQualification = (quotaIdentifier: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: CampaignQualificationUpdateData) =>
      create({ ...data, qualifiable_identifier: quotaIdentifier }),
    {
      // Optimistic update
      onMutate: async (data) => {
        return campaignQualificationUpdater(
          queryClient,
          quotaIdentifier,
          (old) => {
            return {
              ...old,
              data: {
                ...old.data,
                data: [...old.data.data, { attributes: data }],
              },
            } as CQData;
          }
        );
      },

      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, newTodo, context: { previous: unknown }) => {
        queryClient.setQueryData(
          [CACHE_KEYS.CAMPAIGN_QUALIFICATIONS, quotaIdentifier],
          context.previous
        );
      },

      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries([
          CACHE_KEYS.CAMPAIGN_QUALIFICATIONS,
          quotaIdentifier,
        ]);
      },
    }
  );
  return mutation;
};

export const useDestroyCampaignQualification = (
  quotaIdentifier: string,
  identifier: string
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(() => destroy(identifier), {
    // Optimistic update
    onMutate: async () => {
      return campaignQualificationUpdater(
        queryClient,
        quotaIdentifier,
        (old) => {
          const newCqs = old.data.data.reduce((acc, cq) => {
            if (cq.attributes.identifier !== identifier) {
              acc.push(cq);
            }
            return acc;
          }, []);

          return {
            ...old,
            data: {
              ...old.data,
              data: newCqs,
            },
          } as CQData;
        }
      );
    },

    onSuccess: async () => {
      queryClient.refetchQueries([
        CACHE_KEYS.CAMPAIGN_QUALIFICATIONS,
        quotaIdentifier,
      ]);
    },
  });
  return mutation;
};

export const useGetCampaignQualifications = (
  quotaIdentifier: string,
  queryOptions?: unknown
) => {
  return useQuery(
    [CACHE_KEYS.CAMPAIGN_QUALIFICATIONS, quotaIdentifier],
    () =>
      api.insightsPlatform.campaignQualificationsList({
        qualifiable_identifier: quotaIdentifier,
      }),
    queryOptions
  );
};

export const useGetDemographicCampaignQualifications = (
  quotaIdentifier: string,
  queryOptions?: unknown
) => {
  const asset = useAsset();
  const query = useGetCampaignQualifications(quotaIdentifier, queryOptions);

  const cqs = query.isSuccess
    ? query.data.data.data.filter(
        (cq) =>
          ![asset.constants.FS_GENDER_QID, asset.constants.FS_AGE_QID].includes(
            cq.attributes.question_id
          )
      )
    : undefined;

  return { ...query, data: cqs };
};
